import { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  AddCircleOutline,
  Public,
} from "@mui/icons-material";
import IOSSwitch from "../../../components/IOSSwitch";
import axiosInstance from "../../../axios/axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { END_POINTS } from "../../../shared";
import { useEncryptService } from "../../../shared/hooks";
import AntSwitch from "../../../components/AntSwitch";
import { toast } from "react-toastify";

const RescanWithCredentialPopup = ({
  open,
  onClose,
  assetsCredData,
  credData,
  deviceId,
  rescanCallback,
  defaultCreateNew = false,
  agentId,
  agent_id,
  useNewScanApi = false,
  newScanContext,
}: any) => {
  const [isCreatingNewCredential, setIsCreatingNewCredential] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showExecEyePassword, setShowExecEyePassword] = useState(false);

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleToggleExecPasswordVisibility = () =>
    setShowExecEyePassword(!showExecEyePassword);
  const { doEncryptLogin } = useEncryptService();
  const [ppkFile, setPpkFile] = useState<File | null>(null);
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const ppkFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && defaultCreateNew) {
      setIsCreatingNewCredential(true);
      formik.setValues({
        selectedCredential: "create_new",
        credentialType: "ssh",
        passwordType: "password",
        user: "",
        password: "",
        execPassword: "",
        apiToken: "",
        credentialName: "",
        saveCredentials: true,
        autoAssign: false,
        showExecPassword: false,
        execPasswordGroup: "",
        selectedCredenntialId: "create_new",
      });
    } else if (!open) {
      setIsCreatingNewCredential(false);
    }
  }, [open, defaultCreateNew]);

  const resetTouchedFields = () => {
    formik.setTouched({
      selectedCredential: false,
      credentialType: false,
      passwordType: false,
      user: false,
      password: false,
      execPassword: false,
      apiToken: false,
      credentialName: false,
      saveCredentials: false,
      autoAssign: false,
      showExecPassword: false,
      execPasswordGroup: false,
      selectedCredenntialId: false,
    }, false);
  };

  const handleCredentialChange = async (event: any) => {
    const value = event.target.value;

    const isCreateNew = value === "create_new";
    if (isCreateNew) {
      setIsCreatingNewCredential(true);
      setShowExecEyePassword(false);
    } else {
      setIsCreatingNewCredential(false);
    }

    resetTouchedFields();
    await formik.setValues({
      ...formik.values,
      selectedCredential: value,
      credentialType: "ssh",
      passwordType: "none",
      selectedCredenntialId: value === "create_new" ? "create_new" :
        assetsCredData.find((cred: any) => cred.name === value)?.id || "",
      user: isCreateNew ? formik.values.user : "",
      password: isCreateNew ? formik.values.password : "",
      execPassword: isCreateNew ? formik.values.execPassword : "",
      apiToken: isCreateNew ? formik.values.apiToken : "",
      credentialName: isCreateNew ? formik.values.credentialName : "",
      saveCredentials: isCreateNew ? formik.values.saveCredentials : false,
      autoAssign: isCreateNew ? formik.values.autoAssign : false,
      showExecPassword: isCreateNew ? formik.values.showExecPassword : false,
      execPasswordGroup: isCreateNew ? formik.values.execPasswordGroup : "",
    });

    setIsCreatingNewCredential(value === "create_new");
    await formik.validateForm();
  };

  const formik = useFormik({
    initialValues: {
      selectedCredential: "",
      credentialType: "",
      passwordType: "",
      user: "",
      password: "",
      execPassword: "",
      apiToken: "",
      credentialName: "",
      saveCredentials: false,
      autoAssign: false,
      showExecPassword: false,
      execPasswordGroup: "",
      selectedCredenntialId: "",
    },
    validationSchema: Yup.object({

      selectedCredential: Yup.string()
        .required("Credential is required")
        .notOneOf([""], "Credential is required"),

      credentialName: Yup.string().when("selectedCredential", {
        is: "create_new",
        then: (schema) => schema.required("Credential Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),


      saveCredentials: Yup.boolean().when("selectedCredential", {
        is: "create_new",
        then: (schema) => schema.oneOf([true], "Save Credentials must be checked"),
        otherwise: (schema) => schema.notRequired(),
      }),


      apiToken: Yup.string().when(["selectedCredential", "credentialType"], {
        is: (selectedCredential: any, credentialType: any) =>
          selectedCredential === "create_new" && credentialType === "api",
        then: (schema) => schema.required("API Token is required"),
        otherwise: (schema) => schema.notRequired(),
      }),


      user: Yup.string().when(["selectedCredential", "credentialType"], {
        is: (selectedCredential: any, credentialType: any) =>
          selectedCredential === "create_new" &&
          (credentialType === "ssh" || credentialType === "windows"),
        then: (schema) => schema.required("User is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      password: Yup.string().when(
        ["selectedCredential", "credentialType", "passwordType"],
        {
          is: (selectedCredential: any, credentialType: any, passwordType: any) =>
            selectedCredential === "create_new" &&
            ((credentialType === "ssh" && passwordType === "password") ||
              credentialType === "windows"),
          then: (schema) => schema.required("Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      execPasswordGroup: Yup.mixed().when(
        ["selectedCredential", "credentialType", "passwordType"],
        {
          is: (selectedCredential: any, credentialType: any, passwordType: any) =>
            selectedCredential === "create_new" &&
            credentialType === "ssh" &&
            passwordType === "password",
          then: (schema) =>
            schema.test(
              "at-least-one-toggle",
              "Either Auto Assign or Exec Password must be enabled",
              function () {
                const { autoAssign, showExecPassword } = this.parent;
                return autoAssign || showExecPassword;
              }
            ),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      execPassword: Yup.string().when(
        [
          "selectedCredential",
          "credentialType",
          "passwordType",
          "showExecPassword",
        ],
        {
          is: (
            selectedCredential: any,
            credentialType: any,
            passwordType: any,
            showExecPassword: any
          ) =>
            selectedCredential === "create_new" &&
            credentialType === "ssh" &&
            passwordType === "password" &&
            showExecPassword,
          then: (schema) => schema.required("Exec Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }
      ),
    }),

    onSubmit: async (values: any) => {
      try {
        let assetIdList: string[] = [];
        if (Array.isArray(deviceId)) {
          assetIdList = deviceId.filter(Boolean);
        } else if (deviceId) {
          assetIdList = [String(deviceId)];
        }

        const effectiveAgentId = agentId || agent_id;
        if (!effectiveAgentId) {
          toast.error("Agent is mandatory. Please select an Agent.");
          return;
        }

        const payload: any = {
          asset_id: assetIdList,
          new_cred: values.selectedCredential === "create_new" ? true : false,
          agent_id: effectiveAgentId,
        };

        if (!useNewScanApi) {
          payload.rescan_with_cred = "True";
        }

        if (values.selectedCredential === "create_new") {
          payload.loginParameter = values.passwordType;
          payload.cred_name = values.credentialName;
          payload.save = values.saveCredentials;
          payload.global_cred = false;
          payload.description = "";
          payload.encrypt = true;
          if (values.credentialType === "api") {
            const APIType = credData?.find(
              (type: any) => type.code === "api"
            )?.password_type?.find(
              (type: { code: string }) => type.code === "api_token"
            );
            if (APIType) {
              payload.credential_type_id = APIType.id;
            }
            payload.custom_port = false;
            payload.api_token = doEncryptLogin(values.apiToken);
          } else if (values.credentialType === "windows") {
            const windowsTypeObj = credData?.find(
              (type: any) => type.code === "windows"
            )?.password_type?.find(
              (type: { code: string }) => type.code === "password"
            );
            if (windowsTypeObj) {
              payload.credential_type_id = windowsTypeObj.id;
            }
            payload.username = doEncryptLogin(values.user);
            payload.password = doEncryptLogin(values.password);
            payload.auto_assign = true;
            payload.exec_password = false;
          } else {
            payload.username = doEncryptLogin(values.user);
            payload.auto_assign = values.autoAssign;
            if (values.showExecPassword) {
              payload.exec_password = values.showExecPassword;
              payload.enable_password = doEncryptLogin(values.execPassword);
            }
            if (values.passwordType === "password") {
              const passwordType = credData?.find(
                (type: any) => type.code === "ssh"
              )?.password_type?.find(
                (type: { code: string }) => type.code === "password"
              );
              if (passwordType) {
                payload.credential_type_id = passwordType.id;
              }
              payload.password = doEncryptLogin(values.password);
            }
            if (values.passwordType === "private_key") {
              const privateKeyType = credData?.find(
                (type: any) => type.code === "ssh"
              )?.password_type?.find(
                (type: { code: string }) => type.code === "private_key"
              );
              if (privateKeyType && ppkFile) {
                //payload.private_key = ppkFile;
                payload.private_key_name = privateKeyFileName;
                payload.credential_type_id = privateKeyType.id;
                const formData = new FormData();

                Object.entries(payload).forEach(([key, value]) => {
                  if (value === undefined || value === null) {
                    return;
                  }

                  if (Array.isArray(value)) {
                    formData.append(key, JSON.stringify(value));
                    return;
                  }

                  if (typeof value === "boolean") {
                    formData.append(key, value ? "true" : "false");
                    return;
                  }

                  formData.append(key, value as any);
                });

                formData.append("private_key", ppkFile);

                try {
                  if (useNewScanApi) {
                    formData.append("scan_type", newScanContext?.scanType || "onprem_discovery");
                    formData.append("scanname", "");
                    formData.append("description", "");
                    formData.append("ip_range", JSON.stringify(newScanContext?.ipRange || []));
                    formData.append("cred_ids", JSON.stringify([]));
                    formData.append("schedule", "false");
                  }
                  console.log("Payload FormData:", payload);
                  const response = await axiosInstance.post(
                    useNewScanApi ? END_POINTS.NEW_SCAN_TYPES : END_POINTS.RESCAN,
                    formData,
                    {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    }
                  );
                  if (response.status === 200 || response.status === 201) {
                    setIsCreatingNewCredential(false);
                    rescanCallback(response.data?.data?.device_list || response.data);
                    onClose();
                  }
                } catch (error: any) {
                  console.error("Error during rescan with cred:", error.response || error);
                  toast.error(
                    error.response?.data?.message || "Failed to rescan with credential",
                    { type: "error" }
                  );
                }

                return;
              }
              // payload.private_key = data.privateFile;
            }
          }

          if (useNewScanApi) {
            const newScanPayload = {
              ...payload,
              scan_type: newScanContext?.scanType || "onprem_discovery",
              scanname: "",
              description: "",
              ip_range: newScanContext?.ipRange || [],
              cred_ids: [],
              schedule: false,
              save: values.saveCredentials,
            };

            const response = await axiosInstance.post(
              END_POINTS.NEW_SCAN_TYPES,
              newScanPayload
            );
            if (response.status === 200 || response.status === 201) {
              setIsCreatingNewCredential(false);
              rescanCallback(response.data?.data?.device_list || response.data);
              onClose();
            }
            return;
          }
        } else {
          payload.credential_id = values.selectedCredenntialId;
        }

        const response = await axiosInstance.post(
          END_POINTS.RESCAN,
          payload
        );
        if (response.status === 200) {
          console.log("Rescan successful:", response.data);
          formik.resetForm();
          setIsCreatingNewCredential(false);
          rescanCallback(response.data?.data?.device_list);
          onClose();
        } else {
          console.error("Rescan failed:", response.data);
        }
      } catch (error) {
        console.error("Error during rescan:", error);
      }
    },
    validate: (values) => {
      const errors: any = {};
      if (
        values.selectedCredential !== "create_new" &&
        !values.selectedCredential
      ) {
        errors.selectedCredential = "Please select a credential.";
      }
      return errors;
    },
  });

  const handleCredentialTypeChange = async (event: any) => {
    const value = event.target.value;
    resetTouchedFields();
    await formik.setFieldValue("credentialType", value);
    if (value === "ssh") {
      formik.setFieldValue("apiToken", "");
    } else if (value === "api") {
      await formik.setFieldValue("user", "");
      await formik.setFieldValue("passwordType", "none");
      await formik.setFieldValue("password", "");
      await formik.setFieldValue("execPassword", "");
      await formik.setFieldValue("showExecPassword", false);
      await formik.setFieldValue("autoAssign", false);
    } else if (value === "windows") {
      await formik.setFieldValue("apiToken", "");
      await formik.setFieldValue("passwordType", "password");
      await formik.setFieldValue("execPassword", "");
      await formik.setFieldValue("showExecPassword", false);
      await formik.setFieldValue("autoAssign", true);
    }
    await formik.validateForm();
  };

  const handlePasswordTypeChange = async (event: any) => {
    const value = event.target.value;
    resetTouchedFields();
    await formik.setFieldValue("passwordType", value);
    if (value === "private_key" || value === "none") {
      await formik.setFieldValue("execPassword", "");
      await formik.setFieldValue("showExecPassword", false);
      await formik.setFieldValue("autoAssign", false);
      await formik.setFieldValue("password", "");
      setShowExecEyePassword(false);
    }
    await formik.validateForm();
  };

  const handlePpkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file extension
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.pem')) {
        toast.error("Please upload a PEM file (.pem)");
        if (ppkFileInputRef.current) {
          ppkFileInputRef.current.value = '';
        }
        return;
      }
      setPpkFile(file);
      setPrivateKeyFileName(file.name);
    }
  };

  const handlePpkUploadClick = () => {
    ppkFileInputRef.current?.click();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        onClose();
        formik.resetForm();
        setIsCreatingNewCredential(false);
      }} 
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: "550px",
          maxWidth: "90vw",
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {defaultCreateNew ? "Create New Credential" : "Rescan with different credential"}
      </DialogTitle>
      <DialogContent sx={{ overflowY: "auto" }} className="scroll">
        <Typography variant="body2" sx={{ mb: 3, fontSize: "16px" }}>
          {defaultCreateNew ? "Enter credential details below to add and use for rescan." : "Select a credential option to rescan the asset."}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            select
            fullWidth
            label="Select Credentials *"
            value={formik.values.selectedCredential}
            onChange={handleCredentialChange}
            error={
              formik.touched.selectedCredential &&
              Boolean(formik.errors.selectedCredential)
            }
            helperText={
              formik.touched.selectedCredential &&
                typeof formik.errors.selectedCredential === "string"
                ? formik.errors.selectedCredential
                : ""
            }
            sx={{ 
              mb: 2,
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                paddingRight: '32px !important',
              },
              '& .MuiOutlinedInput-root': {
                paddingRight: '0px',
              }
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    mt: 1,
                    minHeight: "100px",
                    maxHeight: "400px",
                  },
                },
              },
              renderValue: (selected) => {
                if (selected === "create_new") {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflow: "hidden",
                      }}
                    >
                      <AddCircleOutline sx={{ color: "blue", fontSize: 20, flexShrink: 0 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Create New Credential
                      </Typography>
                    </Box>
                  );
                }
                const selectedCred = assetsCredData.find((cred: any) => cred.name === selected);
                if (selectedCred) {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflow: "hidden",
                      }}
                    >
                      {selectedCred.global_cred && (
                        <Public sx={{ color: "gray", fontSize: 20, flexShrink: 0 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedCred.name}
                      </Typography>
                    </Box>
                  );
                }
                return selected;
              },
            }}
          >
            <MenuItem value="create_new">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <AddCircleOutline sx={{ color: "blue", fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Create New Credential
                </Typography>
              </Box>
            </MenuItem>

            {assetsCredData.map((cred: any) => (
              <MenuItem 
                key={cred.name} 
                value={cred.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  {cred.global_cred && (
                    <Public sx={{ color: "gray", fontSize: 20, flexShrink: 0 }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                    title={cred.name}
                  >
                    {cred.name}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {isCreatingNewCredential && (
            <>
              <RadioGroup
                row
                value={formik.values.credentialType}
                onChange={handleCredentialTypeChange}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="ssh" control={<Radio />} label="SSH" />
                <FormControlLabel value="api" control={<Radio />} label="API" />
                <FormControlLabel value="windows" control={<Radio />} label="WINDOWS" />
              </RadioGroup>

              {formik.values.credentialType === "ssh" && (
                <>
                  {/* User Input */}
                  <TextField
                    fullWidth
                    label="User *"
                    variant="outlined"
                    value={formik.values.user}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="user"
                    error={formik.touched.user && Boolean(formik.errors.user)}
                    helperText={
                      formik.touched.user &&
                        typeof formik.errors.user === "string"
                        ? formik.errors.user
                        : ""
                    }
                    sx={{ mb: 2 }}
                  />

                  {/* Password Type Radio Buttons */}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Password Type
                  </Typography>
                  <RadioGroup
                    row
                    value={formik.values.passwordType}
                    onChange={handlePasswordTypeChange}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="password"
                      control={<Radio />}
                      label="Password"
                    />
                    <FormControlLabel
                      value="private_key"
                      control={<Radio />}
                      label="Private Key"
                    />
                    {/* <FormControlLabel
                      value="none"
                      control={<Radio />}
                      label="None"
                    /> */}
                  </RadioGroup>

                  {/* Password Input */}
                  {formik.values.passwordType === "password" && (
                    <>
                      <TextField
                        fullWidth
                        label="Password *"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="password"
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password &&
                            typeof formik.errors.password === "string"
                            ? formik.errors.password
                            : ""
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                      {/* Exec Password */}
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          Exec Password
                        </Typography>
                        <IOSSwitch
                          checked={formik.values.showExecPassword}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "showExecPassword",
                              e.target.checked
                            );
                            if (!e.target.checked) {
                              formik.setFieldValue("execPassword", "");
                              formik.setFieldTouched("execPassword", false, false);
                            }
                          }}
                          disabled={formik.values.autoAssign}
                        />
                      </Box>
                      {formik.values.showExecPassword && (
                        <TextField
                          fullWidth
                          label="Exec Password *"
                          type={showExecEyePassword ? "text" : "password"}
                          variant="outlined"
                          value={formik.values.execPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name="execPassword"
                          error={
                            formik.touched.execPassword &&
                            Boolean(formik.errors.execPassword)
                          }
                          helperText={
                            formik.touched.execPassword &&
                              typeof formik.errors.execPassword === "string"
                              ? formik.errors.execPassword
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={handleToggleExecPasswordVisibility}
                              >
                                {showExecEyePassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      {/* Auto Assign */}
                      {!formik.values.showExecPassword && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          sx={{ mb: 2 }}
                        >
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ mr: 2 }}>
                              Auto Assign
                            </Typography>
                            <IOSSwitch
                              checked={formik.values.autoAssign}
                              onChange={async (e) => {
                                await formik.setFieldValue("autoAssign", e.target.checked);
                                if (e.target.checked) {
                                  formik.setFieldValue("execPassword", "");
                                  formik.setFieldValue(
                                    "showExecPassword",
                                    false
                                  );

                                }
                                await formik.validateForm();
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            This will auto-assign Login Password as Exec
                            Password when missing.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {formik.values.passwordType === "private_key" && (
                    <>
                      <Box
                        sx={{
                          border: "1px dashed #3a2ada",
                          padding: "10px",
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="file"
                          accept=".pem"
                          ref={ppkFileInputRef}
                          onChange={handlePpkFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          variant="contained"
                          onClick={handlePpkUploadClick}
                          sx={{
                            backgroundColor: "#3A2AdA",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "#3225b7",
                            },
                          }}
                        >
                          Upload PEM File
                        </Button>
                        {privateKeyFileName && (
                          <Box sx={{ mt: 1, fontSize: "12px", color: "text.secondary" }}>
                            Selected: {privateKeyFileName}
                          </Box>
                        )}
                        <Box>
                          <p>If a PPK file is available, it should be converted into a PEM file</p>
                        </Box>
                      </Box>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <p>Exec Password</p>
                        <AntSwitch
                          checked={formik.values.showExecPassword}
                          onChange={async (e) => {
                            const isChecked = e.target.checked;
                            await formik.setFieldValue("showExecPassword", isChecked);
                            if (!isChecked) {
                              formik.setFieldValue("execPassword", "");
                              formik.setFieldTouched("execPassword", false, false);
                              setShowExecEyePassword(false);
                            }
                          }}
                        />
                      </div>

                      {formik.values.showExecPassword && (
                        <TextField
                          label="Exec Password"
                          type={showExecEyePassword ? "text" : "password"}
                          fullWidth
                          value={formik.values.execPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name="execPassword"
                          error={
                            formik.touched.execPassword &&
                            Boolean(formik.errors.execPassword)
                          }
                          helperText={
                            formik.touched.execPassword &&
                              typeof formik.errors.execPassword === "string"
                              ? formik.errors.execPassword
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <IconButton onClick={handleToggleExecPasswordVisibility}>
                                {showExecEyePassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                    </>
                  )}

                  {/* Private Key Input */}
                  {/* {formik.values.passwordType === "privatekey" && (
                    <>
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          Exec Password
                        </Typography>
                        <IOSSwitch
                          checked={formik.values.showExecPassword}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "showExecPassword",
                              e.target.checked
                            );
                            if (!e.target.checked) {
                              formik.setFieldValue("execPassword", ""); // Clear execPassword when toggled off
                            }
                          }}
                          disabled={formik.values.autoAssign}
                        />
                      </Box>
                      {showExecPassword && (
                        <TextField
                          fullWidth
                          label="Exec Password *"
                          type={showExecEyePassword ? "text" : "password"}
                          variant="outlined"
                          value={formik.values.execPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name="execPassword"
                          error={
                            formik.touched.execPassword &&
                            Boolean(formik.errors.execPassword)
                          }
                          helperText={
                            formik.touched.execPassword &&
                            typeof formik.errors.execPassword === "string"
                              ? formik.errors.execPassword
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={handleToggleExecPasswordVisibility}
                              >
                                {showExecEyePassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                    </>
                  )} */}
                </>
              )}

              {formik.values.credentialType === "api" && (
                <TextField
                  fullWidth
                  label="API Token *"
                  variant="outlined"
                  value={formik.values.apiToken}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="apiToken"
                  error={
                    formik.touched.apiToken && Boolean(formik.errors.apiToken)
                  }
                  helperText={
                    formik.touched.apiToken &&
                      typeof formik.errors.apiToken === "string"
                      ? formik.errors.apiToken
                      : ""
                  }
                  sx={{ mb: 2 }}
                />
              )}

              {formik.values.credentialType === "windows" && (
                <>
                  <TextField
                    fullWidth
                    label="User *"
                    variant="outlined"
                    value={formik.values.user}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="user"
                    error={formik.touched.user && Boolean(formik.errors.user)}
                    helperText={
                      formik.touched.user &&
                        typeof formik.errors.user === "string"
                        ? formik.errors.user
                        : ""
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Password *"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="password"
                    error={
                      formik.touched.password &&
                      Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password &&
                        typeof formik.errors.password === "string"
                        ? formik.errors.password
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                        >
                          {showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Credential Name Input */}
              <TextField
                fullWidth
                label="Credential Name *"
                variant="outlined"
                value={formik.values.credentialName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="credentialName"
                error={
                  formik.touched.credentialName &&
                  Boolean(formik.errors.credentialName)
                }
                helperText={
                  formik.touched.credentialName &&
                    typeof formik.errors.credentialName === "string"
                    ? formik.errors.credentialName
                    : ""
                }
                sx={{ mb: 2 }}
              />

              {/* Save Credentials Checkbox */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={formik.values.saveCredentials}
                  onChange={(e) =>
                    formik.setFieldValue("saveCredentials", e.target.checked)
                  }
                />
                <Typography variant="body2">Save Credentials</Typography>
              </Box>
            </>
          )}
        </form>
      </DialogContent>
      <DialogActions sx={{
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        paddingBottom: "24px",
        // marginTop: "12px",
      }}>
        <Button onClick={() => {
          onClose();
          formik.resetForm();
          setIsCreatingNewCredential(false);
        }} variant="outlined" color="secondary" sx={{ width: 130 }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting}
          sx={{ width: 130 }}
        >
          Connect
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescanWithCredentialPopup;
